/**
 * Per-user UI state (visible table columns, saved filter panels, …)
 * persisted server-side in `UserPreference.ui_state_json`, so choices
 * survive relogin and follow the user across browsers.
 *
 *     const [columns, setColumns] = useUiPreference<string[]>(
 *       "employees.columns",
 *       DEFAULT_COLUMNS,
 *     );
 *
 * localStorage mirrors the value for instant first paint; the server
 * copy wins once `/accounts/user-preferences/me/` resolves. Writes are
 * optimistic — the PATCH failing (offline, expired session) degrades to
 * browser-local persistence, never an error.
 */
import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";

interface UserPreferencePayload {
  ui_state_json?: Record<string, unknown>;
  [key: string]: unknown;
}

const QUERY_KEY = ["ui-prefs", "me"];
const LS_PREFIX = "hwb-ui-pref:";

function readLocal<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}

function writeLocal(key: string, value: unknown): void {
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(value));
  } catch {
    /* private mode — value just won't persist locally */
  }
}

export function useUiPreference<T>(
  key: string,
  defaultValue: T,
): [T, (next: T) => void] {
  const queryClient = useQueryClient();

  const prefs = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      api.get<UserPreferencePayload>("/accounts/user-preferences/me/"),
    staleTime: Infinity,
    retry: 1,
  });

  const serverValue = prefs.data?.ui_state_json?.[key] as T | undefined;
  const value =
    serverValue !== undefined ? serverValue : readLocal<T>(key) ?? defaultValue;

  const setValue = useCallback(
    (next: T) => {
      writeLocal(key, next);
      const current =
        queryClient.getQueryData<UserPreferencePayload>(QUERY_KEY);
      const nextState = { ...(current?.ui_state_json ?? {}), [key]: next };
      queryClient.setQueryData<UserPreferencePayload>(QUERY_KEY, {
        ...(current ?? {}),
        ui_state_json: nextState,
      });
      api
        .patch("/accounts/user-preferences/me/", { ui_state_json: nextState })
        .catch(() => {
          /* server copy missed this write; localStorage still has it */
        });
    },
    [key, queryClient],
  );

  return [value, setValue];
}
